import common from "@/config/common";
import messages from "@/config/messages";
import store from "@/store";
import moment from "moment";
import { NavigationGuardNext } from "vue-router";
import { iBenefitSummary, iSummaryData } from "@/types/iBenefitsData";
import * as apiClient from "@/apiclient/";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const getGenderLabels: { [key: number]: string } = {
  1: "Male",
  2: "Female",
};

const helper = {
  calculateSubsidy(tier: {
    subsidies?: { amount?: number; endDate?: string | null }[];
  }): { subsidyTotal: number; subsidyDetails: string } {
    let subsidyTotal = 0.0;
    let subsidyDetails = "";
    let previousEndDate = "";
    let hasOpenEndDate = false;

    if (tier.subsidies && tier.subsidies.length > 0) {
      tier.subsidies.forEach((subsidy) => {
        subsidyTotal += (subsidy.amount ?? 0) * -1;
        if (!hasOpenEndDate) {
          if (subsidy.endDate == null || subsidy.endDate === "") {
            hasOpenEndDate = true;
          }
          if (previousEndDate !== "" && previousEndDate !== subsidy.endDate) {
            hasOpenEndDate = true;
          }
          previousEndDate = subsidy.endDate ?? "";
        }
      });
    }

    if (hasOpenEndDate) {
      subsidyDetails = "* multiple end dates may apply";
    } else if (previousEndDate) {
      subsidyDetails =
        "* subsidy ends on " + helper.formatDate(previousEndDate);
    }

    return { subsidyTotal, subsidyDetails };
  },
  formatDate(date: string, format = "MM/DD/YYYY"): string {
    return moment(new Date(date)).format(format);
  },
  getGenderLabels(genderId: number): string {
    return getGenderLabels[genderId] || "";
  },
  async getLocationDetails(
    profileCountryId: number,
    profileStateId: number,
    billingCountryId: number,
    billingStateId: number
  ) {
    let getCountryName = "NA";
    let getMonthlyCountryName = "NA";
    let getStateName = "NA";
    let getMonthlyStateName = "NA";
    let countries = {};

    const result = await apiClient.locationController().getApiLocation();
    if (result?.data) {
      if (result.data.countries) {
        getCountryName = this.fetchCountryName(
          result.data.countries,
          profileCountryId
        );
        getMonthlyCountryName = this.fetchCountryName(
          result.data.countries,
          billingCountryId
        );
        result.data.countries.forEach((country: any) => {
          countries = Object.assign(countries, {
            [country.countryCode]: country.countryId,
          });
        });
      }
      if (result.data.states) {
        getStateName = this.fetchStateName(result.data.states, profileStateId);
        getMonthlyStateName = this.fetchStateName(
          result.data.states,
          billingStateId
        );
      }
    }

    return {
      getCountryName,
      getMonthlyCountryName,
      getStateName,
      getMonthlyStateName,
      countries,
    };
  },
  fetchStateName(allStates: any, stateId: number): string {
    if (stateId) {
      const state = allStates.find((data: any) => data.geoStateId == stateId);
      if (state) {
        return state.geoStateName;
      }
    }
    return "NA";
  },
  fetchCountryName(allCountries: any, countryId: number): string {
    if (countryId) {
      const country = allCountries.find(
        (data: any) => data.countryId === countryId
      );
      if (country) {
        return country.countryCode;
      }
    }
    return "NA";
  },
  setPageStep(step: number) {
    const pageStep =
      sessionStorage.getItem(common.storageVariables.pageStep) ?? 0;
    if (Number(pageStep) < step) {
      sessionStorage.setItem(common.storageVariables.pageStep, step.toString());
    }
  },
  formatNumber(amount: number) {
    if (amount === undefined || amount === null) {
      return "0.00";
    } else {
      return amount.toLocaleString("en-US", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  },
  async getGenders() {
    const result = await apiClient.userController().getApiUserGender();
    let genderOptions: apiClient.GenderDto[] = [];
    let validationMsg = "";
    if (!result || result.error) {
      if (result.error) {
        validationMsg = result.error + messages.errorMsg;
      }
    } else if (result.data) {
      genderOptions = result.data;
    }
    return { validationMsg, genderOptions };
  },
  async getStripeEnabled() {
    const result = await apiClient
      .featureManagementController()
      .getApiFeaturemanagementIsstripepaymentenabled();
    let isStripeEnabled = false;
    let validationMsg = "";
    if (!result || result.error) {
      if (result.error) {
        validationMsg = result.error + messages.errorMsg;
      }
      return;
    } else if (result.data !== undefined) {
      isStripeEnabled = result.data!;
    }
    return { validationMsg, isStripeEnabled };
  },
  async getLocations() {
    let allCountries: apiClient.CountryResponseDto[] = [];
    let allStates: apiClient.StateResponseDto[] = [];
    let countries = {};
    const result = await apiClient.locationController().getApiLocation();
    if (result.data !== undefined) {
      if (result.data.countries !== undefined) {
        allCountries = result.data.countries!;
        allCountries.forEach((country: any) => {
          countries = Object.assign(countries, {
            [country.countryCode]: country.countryId,
          });
        });
      }
      if (result.data.states !== undefined) {
        allStates = result.data.states!;
        allStates.sort((a: any, b: any) => {
          if (a.geoStateName < b.geoStateName) {
            return -1;
          }

          if (a.geoStateName > b.geoStateName) {
            return 1;
          }
          return 0;
        });
      }
    }

    return { allCountries, allStates, countries };
  },
  beforeRouterLeave(next: any) {
    if (store.getters.getFormDirty) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        store.commit("setFormDirty", false);
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  confirmBeforeUnload(event: any) {
    if (store.getters["getFormDirty"]) {
      event.preventDefault();
      event.returnValue = messages.dirtyFormCheckMsg;
    }
  },
  async printPDF(
    content: HTMLElement,
    isDownload: boolean,
    convertAsImage: boolean
  ): Promise<Blob> {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Define the margins (in mm)
    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth(); // A4 width in mm (210mm)
    const pageHeight = doc.internal.pageSize.getHeight(); // A4 height in mm (297mm)

    // Adjust the available content area by subtracting the margins
    const contentWidth = pageWidth - 2 * margin; // Subtract margins on both sides
    const contentHeight = pageHeight - 2 * margin; // Subtract margins on top and bottom

    if (content) {
      if (convertAsImage) {
        // Use html2canvas to render the content as an image
        const canvas = await html2canvas(content, {
          logging: true, // Enable logging to help debug
          useCORS: true, // Enable CORS for external images
          scale: 2, // Increase scale for better resolution,
        }).catch((error) => {
          console.error("html2canvas failed: ", error);
          return null; // In case of failure
        });

        // Check if the canvas is null or empty
        if (!canvas || !canvas.width || !canvas.height) {
          console.error("Canvas is empty or not rendered.");
          return Promise.reject(new Error("Canvas rendering failed."));
        }
        const imgData = canvas.toDataURL("image/jpeg", 2.0); // Convert canvas to image data
        // Convert canvas dimensions (in px) to mm (1 px ≈ 0.264583 mm)
        const pxToMm = 0.264583;
        const imgWidth = canvas.width * pxToMm; // Convert width to mm
        const imgHeight = canvas.height * pxToMm; // Convert height to mm

        // Calculate scaling factor to make sure content fits on one page (without breaks)
        const scaleX = contentWidth / imgWidth;
        const scaleY = contentHeight / imgHeight;
        const scale = Math.min(scaleX, scaleY); // Use the smaller of the two scale factors to ensure it fits

        // Scale the image dimensions to fit the page
        const scaledImgWidth = imgWidth * scale;
        const scaledImgHeight = imgHeight * scale;

        // Draw the image on the PDF with margins considered
        doc.addImage(
          imgData,
          "JPEG",
          margin,
          margin,
          scaledImgWidth,
          scaledImgHeight
        );

        // After rendering, save or return the PDF
        const pdfBlob = doc.output("blob");

        if (isDownload) {
          const link = document.createElement("a");
          const url = URL.createObjectURL(pdfBlob);
          link.href = url;
          link.download = "confirmation.pdf";
          link.click();
        }

        // Resolve the outer promise with the pdfBlob
        return pdfBlob;
      } else {
        const contentWidth = 790; // in pixels
        const contentHeight = content.offsetHeight; // in pixels

        const pageWidth = doc.internal.pageSize.getWidth(); // A4 width in mm (210mm)
        const pageHeight = doc.internal.pageSize.getHeight(); // A4 height in mm (297mm)

        // Convert contentWidth and contentHeight from pixels to mm (1px ≈ 0.264583mm)
        const pxToMm = 0.264583;
        const contentWidthMm = contentWidth * pxToMm;
        const contentHeightMm = contentHeight * pxToMm;

        // Calculate scaling factor to fit content into the A4 page size
        const scaleX = pageWidth / contentWidthMm;
        const scaleY = pageHeight / contentHeightMm;
        const scale = Math.min(scaleX, scaleY);

        return new Promise((resolve, reject) => {
          doc.html(content, {
            x: 10,
            y: 10,
            width: pageWidth,
            windowWidth: contentWidth - 20,
            callback: (doc) => {
              const pdfBlob = doc.output("blob");

              if (isDownload) {
                const link = document.createElement("a");
                const url = URL.createObjectURL(pdfBlob);
                link.href = url;
                link.download = "confirmation.pdf";
                link.click();
              }

              // Resolve the outer promise with the pdfBlob
              resolve(pdfBlob);
            },
            autoPaging: true,
          });
        });
      }
    } else {
      return Promise.reject(new Error("Content element not found"));
    }
  },
  setBenefitSummary(benefitSummaryData: apiClient.BenefitSummaryDto[]) {
    const benefitSummary: iBenefitSummary = {
      amountDueToday: 0,
      monthlyPremium: 0,
      amountDuePlanRates: [],
      monthlyPremiumPlanRates: [],
    };
    benefitSummaryData.forEach((total: apiClient.BenefitSummaryDto) => {
      benefitSummary.amountDueToday += total.initialAmount!;
      benefitSummary.monthlyPremium += total.monthlyPremium!;
      benefitSummary.amountDuePlanRates.push({
        benefitPlanName: total.planDescription!,
        rate: total.initialAmount!,
      });
      benefitSummary.monthlyPremiumPlanRates.push({
        benefitPlanName: total.planDescription!,
        rate: total.monthlyPremium!,
      });
    });
    store.commit("setBenefitRates", benefitSummary);
  },
  base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  },
};

export default helper;
